<template>
  <div class="w-full relative">
    <div
        v-show="isBusy"
        class="absolute inset-0 z-20 flex items-center justify-center bg-white/70 backdrop-blur-sm"
    >
      <div class="w-full px-6">
        <div class="flex flex-col items-center justify-center gap-4">
          <Spinner :color="'text-black'" :size="10" />
          <div class="text-sm text-gray-600 text-center">
            Please wait...
          </div>
        </div>
      </div>
    </div>

    <div class="w-full">
      <div class="flex items-start justify-between gap-4 pb-4 border-b border-gray-200">
        <div>
          <div class="text-sm uppercase tracking-wide font-semibold text-primary">
            Documents
          </div>
          <div class="text-xs text-gray-500 mt-1">
            Attach an existing document or upload a new one.
          </div>
        </div>

        <div
            v-if="isEditMode"
            class="shrink-0 inline-flex items-center text-[11px] px-2 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-200"
        >
          Edit mode
        </div>
        <div
            v-else
            class="shrink-0 inline-flex items-center text-[11px] px-2 py-1 rounded-full bg-gray-50 text-gray-700 border border-gray-200"
        >
          Add mode
        </div>
      </div>

      <div class="pt-5 space-y-6">
        <div v-if="!isEditMode" class="space-y-4">
          <div class="border border-gray-200 rounded-lg p-4">
            <div class="flex items-start justify-between gap-3 mb-4">
              <div>
                <div class="text-xs uppercase tracking-wide font-bold text-gray-800">
                  Attach existing document
                </div>
                <div class="text-xs text-gray-500 mt-1">
                  Search and select one or more documents already available.
                </div>
              </div>
              <div class="text-[11px] font-semibold px-2 py-1 rounded-full bg-gray-50 text-gray-700 border border-gray-200">
                Existing
              </div>
            </div>

            <label class="block text-[11px] uppercase tracking-wide font-bold text-gray-700 mb-2">
              Select existing document <span class="text-red-500">*</span>
            </label>

            <AutoComplete
                :multiple="true"
                v-model="selectedDocuments"
                field="document_number"
                :suggestions="filteredDocuments"
                @complete="searchDocuments($event)"
                placeholder="Type to search documents..."
                class="w-full"
            >
              <template #item="{ item }">
                <div class="ac-row">
                  <div class="ac-code" :title="item.document_number">{{ item.document_number }}</div>
                  <div class="ac-name" :title="item.name">{{ item.name }}</div>

                  <div class="ac-type">
                    <i :class="docTypeIcon(item)" class="pi ac-type-icon"></i>
                    <span class="ac-type-text">{{ docTypeLabel(item) }}</span>
                  </div>

                  <div class="ac-cat" :title="item.category">{{ item.category }}</div>
                </div>
              </template>
            </AutoComplete>

            <div class="mt-3 flex items-center justify-between text-xs text-gray-500">
              <span>Tip: You can pick multiple documents.</span>
              <span v-if="selectedDocuments && selectedDocuments.length" class="text-gray-700 font-semibold">
                Selected: {{ selectedDocuments.length }}
              </span>
            </div>

            <div class="relative flex justify-end mt-4">
              <div v-show="!canAddExistingDocuments" class="absolute inset-0 bg-white/60 rounded"></div>

              <button
                  class="flex items-center bg-blue-200 hover:bg-blue-300 text-secondary py-1 px-3 rounded border border-blue-400 hover:border-blue-500 hover:shadow active:shadow-inner transition duration-100"
                  :disabled="isBusy || !canAddExistingDocuments"
                  @click="addExistingDocuments"
              >
                <svg
                    class="stroke-current h-5 w-5 mr-2"
                    fill="none"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    viewBox="0 0 24 24"
                >
                  <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                  <path d="M17 21v-8H7v8M7 3v5h8" />
                </svg>
                <span>Attach selected</span>
              </button>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <div class="flex-1 h-px bg-gray-200"></div>
            <div class="text-[11px] uppercase tracking-wide font-bold text-gray-500">OR</div>
            <div class="flex-1 h-px bg-gray-200"></div>
          </div>
        </div>

        <div class="border border-gray-200 rounded-lg p-4">
          <div class="flex items-start justify-between gap-3 mb-4">
            <div>
              <div class="text-xs uppercase tracking-wide font-bold text-gray-800">
                {{ isEditMode ? "Edit document details" : "Upload new document" }}
              </div>
              <div class="text-xs text-gray-500 mt-1">
                {{ isEditMode ? "Update the name and description." : "Choose a file and save it to this product." }}
              </div>
            </div>

            <div
                class="text-[11px] font-semibold px-2 py-1 rounded-full border"
                :class="isEditMode ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-green-50 text-green-700 border-green-200'"
            >
              {{ isEditMode ? "Edit" : "Upload" }}
            </div>
          </div>

          <div class="space-y-4">
            <div>
              <label class="block text-[11px] uppercase tracking-wide font-bold text-gray-700 mb-2">
                Document name <span class="text-red-500">*</span>
              </label>
              <input
                  class="bge-input bge-input-spacing w-full rounded"
                  v-model="newDocument.name"
                  placeholder="Document Name..."
                  type="text"
              />
            </div>

            <div>
              <label class="block text-[11px] uppercase tracking-wide font-bold text-gray-700 mb-2">
                Document description <span class="text-red-500">*</span>
              </label>
              <textarea
                  v-model="newDocument.description"
                  class="bge-input bge-input-spacing w-full rounded"
                  placeholder="Document Description..."
                  rows="8"
              ></textarea>
            </div>

            <div v-if="!isEditMode" class="space-y-2">
              <label class="block text-[11px] uppercase tracking-wide font-bold text-gray-700">
                File <span class="text-red-500">*</span>
              </label>
              <div class="w-full">
                <FileUpload :file.sync="newDocument.file" :accept="docAccept" />
              </div>
            </div>

            <div v-if="isUploading" class="w-full">
              <ProgressBar :progress="uploadProgress" />
              <div class="mt-2 text-xs text-gray-600 text-center">
                {{ uploadProgress }}%
              </div>
            </div>

            <div class="relative flex justify-end pt-1">
              <div v-show="!canSubmit" class="absolute inset-0 bg-white/60 rounded"></div>

              <button
                  :disabled="isBusy || !canSubmit"
                  type="button"
                  class="flex items-center bg-blue-200 hover:bg-blue-300 text-secondary py-1 px-3 rounded border border-blue-400 hover:border-blue-500 hover:shadow active:shadow-inner transition duration-100"
                  @click="onSave"
              >
                <svg
                    class="stroke-current h-5 w-5 mr-2"
                    fill="none"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    viewBox="0 0 24 24"
                >
                  <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                  <path d="M17 21v-8H7v8M7 3v5h8" />
                </svg>
                <span>Save</span>
              </button>
            </div>

            <div v-if="isEditMode" class="text-xs text-gray-500">
              Upload is disabled while editing an existing document.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import notificationMixin from "@/mixins/notificationMixin";

const Spinner = () => import("@/components/Spinner.vue");
const FileUpload = () => import("@/components/FileUpload.vue");
const ProgressBar = () => import("@/components/ProgressBar.vue");

export default {
  name: "AddDocuments",
  mixins: [notificationMixin],
  components: { Spinner, FileUpload, ProgressBar },
  props: {
    product_id: { type: String, required: true },
    mode: { type: String, default: "add" },
    document: { type: Object, default: null },
  },
  data() {
    return {
      selectedDocuments: [],
      filteredDocuments: null,
      newDocument: {
        name: "",
        description: "",
        file: null,
      },
      isBusy: false,
      isUploading: false,
      uploadProgress: 0,
    };
  },
  computed: {
    isEditMode() {
      return this.mode === "edit" && this.document && this.document.document_id;
    },
    canAddExistingDocuments() {
      return this.selectedDocuments.length > 0;
    },
    docAccept() {
      return ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.zip,.rar,.7z";
    },
    canSubmit() {
      if (this.isEditMode) {
        return !!(this.newDocument.name && this.newDocument.description);
      }

      const f = this.getSelectedFile();
      if (!f) return false;

      if (this.isImageFile(f)) return false;

      return !!(this.newDocument.name && this.newDocument.description);
    },
  },
  watch: {
    "newDocument.file": function () {
      if (this.isEditMode) return;

      const f = this.getSelectedFile();
      if (!f) return;

      if (this.isImageFile(f)) {
        this.notifyError("Images are not allowed here. Please upload a document file.");
        this.newDocument.file = null;
      }
    },
  },
  mounted() {
    if (this.isEditMode) {
      this.newDocument.name = (this.document && this.document.name) || "";
      this.newDocument.description = (this.document && this.document.description) || "";
      this.newDocument.file = null;
    }
  },
  methods: {
    getSelectedFile() {
      const f = this.newDocument.file;
      if (!f) return null;

      if (Array.isArray(f)) return f.length > 0 ? f[0] : null;

      if (typeof File !== "undefined" && f instanceof File) return f;

      if (typeof f === "object" && typeof f.length === "number") {
        return f.length > 0 ? f[0] : null;
      }

      if (f && f.files && Array.isArray(f.files)) {
        return f.files.length > 0 ? f.files[0] : null;
      }

      if (f && typeof File !== "undefined" && f.file instanceof File) return f.file;

      if (f && f.name && f.size) return f;

      return null;
    },

    getFileExtensionFromName(filename) {
      if (!filename) return "";
      const parts = String(filename).split(".");
      if (parts.length < 2) return "";
      return String(parts.pop()).toLowerCase();
    },

    ensureFilenameHasExtension(filename, ext) {
      const hasExt = this.getFileExtensionFromName(filename);
      if (hasExt) return filename;

      const safeExt = (ext || "").toLowerCase().replace(".", "");
      if (!safeExt) return filename;

      return `${filename}.${safeExt}`;
    },

    getExtFromFile(fileObj) {
      if (!fileObj) return "";

      const name = fileObj.name ? String(fileObj.name) : "";
      const nameExt = this.getFileExtensionFromName(name);
      if (nameExt) return nameExt;

      const mime = fileObj.type ? String(fileObj.type).toLowerCase() : "";
      if (!mime) return "";

      const parts = mime.split("/");
      return parts.length === 2 ? parts[1] : mime;
    },

    isImageFile(fileObj) {
      if (!fileObj) return false;

      const mime = fileObj.type ? String(fileObj.type).toLowerCase() : "";
      if (mime && mime.indexOf("image/") === 0) return true;

      const ext = this.getExtFromFile(fileObj);
      return (
          ext === "png" ||
          ext === "jpg" ||
          ext === "jpeg" ||
          ext === "webp" ||
          ext === "gif" ||
          ext === "bmp" ||
          ext === "svg"
      );
    },

    async searchDocuments(event) {
      try {
        const query =
            event && typeof event.query === "string" ? String(event.query).toLowerCase() : "";

        const result = await this.DocumentService.searchDocuments(query);
        this.filteredDocuments = result && result.data ? result.data : [];
      } catch (error) {
        console.error("Error searching documents:", error);
        this.notifyError("Unable to search for documents.");
      }
    },

    async addExistingDocuments() {
      try {
        this.isBusy = true;

        const promises = this.selectedDocuments.map((d) => {
          return this.ProductServiceV2.attachExistingProductDocument(
              this.product_id,
              d.document_id,
              "document"
          );
        });

        await Promise.all(promises);

        this.selectedDocuments = [];
        this.notifySuccess("Documents added successfully.");
        this.$emit("complete");
      } catch (error) {
        console.error("Error adding documents:", error);
        this.notifyError("Unable to add documents.");
      } finally {
        this.isBusy = false;
      }
    },

    async onSave() {
      if (!this.canSubmit) {
        const f = this.getSelectedFile();
        if (f && this.isImageFile(f)) {
          this.notifyError("Images are not allowed here. Please upload a document file.");
        }
        return;
      }

      if (this.isEditMode) return this.updateDocument();
      return this.createAndUploadToBlob();
    },

    async updateDocument() {
      try {
        this.isBusy = true;

        await this.ProductServiceV2.updateProductDocument(
            this.product_id,
            this.document.document_id,
            {
              name: this.newDocument.name,
              description: this.newDocument.description,
            }
        );

        this.notifySuccess("Document updated successfully.");
        this.$emit("complete");
      } catch (error) {
        console.error("Error updating product document:", error);
        this.notifyError("Unable to update document.");
      } finally {
        this.isBusy = false;
      }
    },

    async createAndUploadToBlob() {
      const fileObj = this.getSelectedFile();
      if (!fileObj) {
        this.notifyError("Please choose a file to upload.");
        return;
      }

      if (this.isImageFile(fileObj)) {
        this.notifyError("Images are not allowed here. Please upload a document file.");
        this.newDocument.file = null;
        return;
      }

      try {
        this.isBusy = true;
        this.isUploading = true;
        this.uploadProgress = 0;

        const ext = this.getExtFromFile(fileObj);
        const originalName = fileObj.name ? String(fileObj.name) : "document";
        const safeFilename = this.ensureFilenameHasExtension(originalName, ext);

        const payload = [
          {
            filename: safeFilename,
            content_type: (fileObj.type && String(fileObj.type)) || "application/octet-stream",
            visibility: "Internal",
            category: "products",
            document_role: "document",
            requires_thumbnail: false,
            unique_id: `${Date.now()}`,
            name: this.newDocument.name,
            description: this.newDocument.description,
          },
        ];

        const created = await this.ProductServiceV2.uploadProductDocuments(
            payload,
            this.product_id,
            "document"
        );

        const createdDoc = Array.isArray(created)
            ? created.length > 0
                ? created[0]
                : null
            : created;

        const documentId = createdDoc && createdDoc.document_id ? createdDoc.document_id : null;
        const accessUrl = createdDoc && createdDoc.access_url ? createdDoc.access_url : null;

        if (!documentId || !accessUrl) {
          this.notifyError("Upload URL not returned from server.");
          return;
        }

        if (typeof AzureStorage === "undefined" || !AzureStorage || !AzureStorage.Blob) {
          this.notifyError("AzureStorage client not available in UI.");
          return;
        }

        const url = new URL(accessUrl);
        const accountUrl = url.origin;
        const sasToken = url.search;

        const pathParts = url.pathname.split("/").filter((p) => p && String(p).trim() !== "");
        const container = pathParts.length > 0 ? pathParts[0] : "";
        const blobPath = pathParts.slice(1).join("/");

        const blobService = AzureStorage.Blob.createBlobServiceWithSas(accountUrl, sasToken);

        const customBlockSize =
            fileObj.size > 1024 * 1024 * 32 ? 1024 * 1024 * 4 : 1024 * 128;

        blobService.singleBlobPutThresholdInBytes = customBlockSize;

        const uploadSummary = blobService.createBlockBlobFromBrowserFile(
            container,
            blobPath,
            fileObj,
            { blockSize: customBlockSize },
            async (error) => {
              if (error) {
                this.isUploading = false;
                this.isBusy = false;
                this.uploadProgress = 0;
                this.notifyError("There was a problem uploading your document to storage.");
                return;
              }

              try {
                await this.ProductServiceV2.updateProductDocument(this.product_id, documentId, {
                  is_uploaded: true,
                });

                this.notifySuccess("Document uploaded successfully.");
                this.$emit("complete");

                this.newDocument.name = "";
                this.newDocument.description = "";
                this.newDocument.file = null;
              } catch (e) {
                console.error("Error finalising document upload:", e);
                this.notifyError("Upload completed but failed to finalise document.");
              } finally {
                this.isUploading = false;
                this.isBusy = false;
                this.uploadProgress = 0;
              }
            }
        );

        if (uploadSummary && typeof uploadSummary.on === "function") {
          uploadSummary.on("progress", () => {
            if (uploadSummary && typeof uploadSummary.getCompletePercent === "function") {
              this.uploadProgress = parseFloat(uploadSummary.getCompletePercent());
            }
          });
        }
      } catch (error) {
        console.error("Error uploading product document:", error);
        this.notifyError("Unable to upload document.");
        this.isUploading = false;
        this.uploadProgress = 0;
        this.isBusy = false;
      }
    },

    getContentType(doc) {
      const ct =
          (doc && doc.content_type) ||
          (doc && doc.mime_type) ||
          (doc && doc.contentType) ||
          "";
      return ct ? String(ct).toLowerCase() : "";
    },

    docTypeLabel(doc) {
      const filename =
          (doc && doc.original_filename && String(doc.original_filename)) ||
          (doc && doc.filename && String(doc.filename)) ||
          "";

      const ext = this.getFileExtensionFromName(filename);
      if (ext) return ext.toUpperCase();

      const ct = this.getContentType(doc);
      if (ct) return String(ct).toUpperCase();

      return "FILE";
    },

    docTypeIcon(doc) {
      const ext = this.docTypeLabel(doc).toLowerCase();

      if (ext === "pdf") return "pi pi-file-pdf";

      if (
          ext === "png" ||
          ext === "jpg" ||
          ext === "jpeg" ||
          ext === "webp" ||
          ext === "gif" ||
          ext === "bmp" ||
          ext === "svg"
      ) return "pi pi-image";

      if (ext === "doc" || ext === "docx") return "pi pi-file-word";

      if (ext === "xls" || ext === "xlsx" || ext === "csv") return "pi pi-file-excel";

      if (ext === "txt" || ext === "rtf") return "pi pi-file";

      if (ext === "zip" || ext === "rar" || ext === "7z") return "pi pi-folder";

      return "pi pi-file";
    },
  },
};
</script>

<style scoped>
.autocomplete-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}
.autocomplete-code,
.autocomplete-name,
.autocomplete-type,
.autocomplete-category {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.autocomplete-code {
  width: 18%;
  font-weight: 800;
  color: #111827;
}
.autocomplete-name {
  width: 57%;
  color: #374151;
}
.autocomplete-type {
  width: 10%;
  display: flex;
  justify-content: center;
}
.autocomplete-category {
  width: 15%;
  text-align: right;
  color: #6b7280;
}

.ac-row{
  display:grid;
  grid-template-columns: 140px 1fr 80px 60px;
  gap:12px;
  align-items:center;
}

.ac-code,.ac-name,.ac-cat{
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.ac-code{ font-weight:800; color:#111827; }
.ac-name{ color:#374151; }
.ac-cat{ color:#6b7280; text-align:right; }

.ac-type{
  display:flex;
  align-items:center;
  justify-content:flex-start;
  gap:8px;
  white-space:nowrap;
}

.ac-type-icon{
  width:18px;
  min-width:18px;
  display:inline-flex;
  justify-content:flex-start;
}

.ac-type-text{
  color:#374151;
}
</style>
